/** tm/components/RegisterClientModal.tsx — 업체 등록 모달 (사업자번호 조회 포함)
 *  2026-10-12 업무진행 설계 §8 입력 강제: 「소개 경로 *」 4갈래(FP · 세무사무소 · 지인 · 직접 문의) — 기본값 없음
 *   FP → 담당 FP * · WM 은 FP 에서 자동(없으면 「WM 선택 / WM 없음」 * — 고른 WM 은 FP 에도 저장)
 *   세무사무소 → 사무소 * · 담당자 · 연락처(전에 적은 사무소 자동완성) · 지인 → 소개한 분 * · 연락처 · 직접 문의 → 어떻게(선택)
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { Company, FP, WM } from '../types';
import { C, T, R, Btn, Field } from './ui';
import { TagEditor } from './TagEditor';
import PersonPicker from './PersonPicker';
import * as api from '../api';
import CompanyMatchFields, { type MatchFieldsValue, emptyMatchFields, mergeTags } from './CompanyMatchFields';

interface Props {
  fps: FP[];
  wms: WM[];
  onSave: () => void;
  onClose: () => void;
  onToast: (msg: string, type?: 'success' | 'error') => void;
  onRefresh?: () => Promise<void>;
  defaultFpId?: string;
  /** WM 관리 「직접 거래처」에서 열면 그 WM님(FP 없이 WM님이 맡는 업체 · 2026-10-29) */
  defaultWmId?: string;
  /** 전에 적은 세무사무소 이름 자동완성용 */
  companies?: Company[];
}

type Referral = NonNullable<Company['referral_type']>;
const REFERRALS: Referral[] = ['FP', '세무사무소', '지인', '직접 문의'];
const HOWS = ['홈페이지', '강의', '전화 문의', '기타'];

/** 담당 줄(FP · WM) 모양 — 한 상자 안에 같은 높이 · 같은 글씨 (2026-10-26) */
const personRow = (line: boolean): CSSProperties => ({ display: 'grid', gridTemplateColumns: '32px minmax(0,1fr) auto', alignItems: 'center', gap: 10, minHeight: 52, padding: '6px 12px', borderTop: line ? `1px solid ${C.border}` : 'none' });
const avatar = (color: string): CSSProperties => ({ width: 32, height: 32, borderRadius: '50%', background: color, color: '#fff', fontSize: 13, fontWeight: 700, display: 'grid', placeItems: 'center' });
const rowBtn: CSSProperties = { height: 30, padding: '0 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.sm, cursor: 'pointer', fontFamily: 'inherit' };

export default function RegisterClientModal({ fps, wms, onSave, onClose, onToast, onRefresh, defaultFpId, defaultWmId, companies }: Props) {
  // 소개 경로 — FP 화면에서 열면 FP(그 FP 가 이미 골라져 있다), 그 밖에는 기본값 없이 꼭 고르게
  const [referral, setReferral] = useState<Referral | ''>(defaultFpId ? 'FP' : '');
  const [refDetail, setRefDetail] = useState({ office: '', person: '', phone: '', how: '' });
  const [wmChoice, setWmChoice] = useState('');
  const [addFp, setAddFp] = useState(false);   // 「+ 함께 맡는 FP 추가」를 눌러 검색 칸을 연 상태   // FP 에 WM 이 없을 때: WM id 또는 'none'
  const offices = useMemo(() => Array.from(new Set((companies || []).map(c => (c.referral_type === '세무사무소' && c.referral_detail && c.referral_detail.office) || '').filter(Boolean))).sort(), [companies]);
  const [form, setForm] = useState({
    biz_no: '', name: '', biz_type_detail: '', corp_type: '',
    ceo: '', ceo_phone: '', ceo_email: '', address: '',
    fp_ids: defaultFpId ? [defaultFpId] : [] as string[], tags: [] as string[],
    established_year: null as number | null,
    company_reg_num: '',
    wm_id: '',
  });
  // 매칭 정보(지역·업종·기업규모·창업기업·대표나이·연매출·직원수·업력·분류·전용분야) — CompanyMatchFields 공용
  const [matchValue, setMatchValue] = useState<MatchFieldsValue>(emptyMatchFields());
  const [matchOpen, setMatchOpen] = useState(true);
  const [niceLoading, setNiceLoading] = useState(false);
  // bizInfo state removed — lookup results fill form fields directly
  const [saving, setSaving] = useState(false);
  // 이 모달에서 막 등록한 FP · WM — 상위 목록(props)이 다시 불려 오기 전에도 칩 이름 · 목록에 보이게
  const [localFps, setLocalFps] = useState<FP[]>([]);
  const [localWms, setLocalWms] = useState<WM[]>([]);
  const allFps = useMemo(() => { const ids = new Set(fps.map(f => f.id)); return fps.concat(localFps.filter(f => !ids.has(f.id))); }, [fps, localFps]);
  const allWms = useMemo(() => { const ids = new Set(wms.map(w => w.id)); return wms.concat(localWms.filter(w => !ids.has(w.id))); }, [wms, localWms]);
  // 고른 WM 소속 FP 를 앞에 — PersonPicker 는 검색어가 없을 때 이 순서 그대로 보여 준다
  const fpOptions = allFps;
  // 첫 담당 FP 의 WM — 있으면 자동, 없으면 wmChoice 로 고르게
  const firstFp = form.fp_ids.length ? allFps.find(f => f.id === form.fp_ids[0]) : undefined;
  const fpWm = firstFp && firstFp.wm_id ? allWms.find(w => w.id === firstFp.wm_id) : undefined;
  const needWm = referral === 'FP' && !!firstFp && !firstFp.wm_id;
  // 첫 FP 가 바뀌면 전에 고른 WM 은 비운다(다른 FP 에 엉뚱한 WM 이 저장되지 않게 · 2026-10-12 검수)
  const firstFpId = form.fp_ids[0] || '';
  useEffect(() => { setWmChoice(''); }, [firstFpId]);
  function refMissing(): string {
    if (!referral) return '소개 경로를 고르세요';
    if (referral === 'FP' && !form.fp_ids.length) return '담당 FP를 선택하세요';
    if (needWm && !wmChoice) return 'WM을 고르거나 「WM 없음」을 누르세요';
    if (referral === '세무사무소' && !refDetail.office.trim()) return '세무사무소 이름을 적으세요';
    if (referral === '지인' && !refDetail.person.trim()) return '소개한 분 이름을 적으세요';
    return '';
  }

  // Auto-lookup when biz_no reaches 10 digits
  const autoLookupDone = useRef('');
  const lookupInProgress = useRef(false);
  useEffect(() => {
    const cleaned = form.biz_no.replace(/\D/g, '');
    if (cleaned.length === 10 && autoLookupDone.current !== cleaned) {
      autoLookupDone.current = cleaned;
      lookupBizNo();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.biz_no]);

  function upd<K extends keyof typeof form>(k: K, v: typeof form[K]) {
    setForm(p => ({ ...p, [k]: v }));
  }

  // 사업자번호 자동 포맷
  function formatBizNo(v: string) {
    const nums = v.replace(/\D/g, '').slice(0, 10);
    if (nums.length <= 3) return nums;
    if (nums.length <= 5) return nums.slice(0, 3) + '-' + nums.slice(3);
    return nums.slice(0, 3) + '-' + nums.slice(3, 5) + '-' + nums.slice(5);
  }

  // 사업자번호 조회 (팝빌 CheckBizInfo)
  async function lookupBizNo() {
    const cleaned = form.biz_no.replace(/\D/g, '');
    if (cleaned.length !== 10) { onToast('사업자번호 10자리를 입력하세요', 'error'); return; }
    if (lookupInProgress.current) return;
    lookupInProgress.current = true;
    setNiceLoading(true);
    try {
      const res = await api.bizLookup(cleaned);
      const d = res.data;
      if (!d) throw new Error('조회 결과 없음');
      // 자동 채움 — 기본 정보
      setForm(p => ({
        ...p,
        name: d.name || p.name,
        ceo: d.ceo || p.ceo,
        corp_type: d.person_corp || p.corp_type,
        address: d.address || p.address,
        established_year: d.established_year || p.established_year,
        company_reg_num: d.company_reg_num || p.company_reg_num,
      }));
      // 자동 채움 — 매칭 정보: 개업일→설립일 · 업태→업종(자유텍스트+7라벨 추정) · 주소→지역/시군구
      setMatchValue(p => ({
        ...p,
        established_date: popbillDateToISO(d.established_date) || p.established_date,
        biz_type: d.biz_class || p.biz_type,
        biz_types: p.biz_types.length ? p.biz_types : (guessBizCategory(d.biz_class) ? [guessBizCategory(d.biz_class)] : []),
        region: guessRegion(d.address) || p.region,
        region_detail: p.region_detail || guessRegionDetail(d.address),
      }));
      const stateMsg = d.close_state && d.close_state !== '사업중' ? ` (${d.close_state})` : '';
      const scaleMsg = d.corp_scale ? ` · ${d.corp_scale}` : '';
      // bizInfo display removed — results fill form directly
      onToast(`${d.name} · ${d.ceo}${scaleMsg}${stateMsg}`);
    } catch (e: unknown) {
      onToast('업체 정보를 조회할 수 없습니다. 직접 입력해주세요.', 'error');
    } finally { setNiceLoading(false); lookupInProgress.current = false; }
  }

  // 팝빌 개업일(YYYYMMDD, 구분자 없음) → HTML date input 형식(YYYY-MM-DD)
  function popbillDateToISO(d: string): string {
    const s = String(d || '').replace(/[^0-9]/g, '');
    return s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : '';
  }
  // 주소에서 지역 추정
  function guessRegion(addr: string): string {
    if (!addr) return '';
    const map: [string, string][] = [['서울','서울'],['경기','경기'],['인천','인천'],['부산','부산'],['대구','대구'],['대전','대전'],['광주','광주'],['울산','울산'],['세종','세종'],['강원','강원'],['충북','충북'],['충남','충남'],['전북','전북'],['전남','전남'],['경북','경북'],['경남','경남'],['제주','제주']];
    for (const [k, v] of map) { if (addr.includes(k)) return v; }
    return '';
  }
  // 주소 두 번째 토큰(시/군/구) 추정 — backend normalizeRegion() 과 같은 방식
  function guessRegionDetail(addr: string): string {
    const parts = (addr || '').trim().split(/\s+/);
    return parts[1] || '';
  }

  // 업종 문자열에서 대분류 추정
  function guessBizCategory(bizClass: string): string {
    if (!bizClass) return '';
    if (/도매|소매|판매|유통/.test(bizClass)) return '도소매업';
    if (/제조|생산|가공/.test(bizClass)) return '제조업';
    if (/연구\s*개발|R&D|연구\s*용역|엔지니어링\s*서비스/i.test(bizClass)) return '연구개발업';
    if (/소프트|IT|정보|통신|인터넷|개발/.test(bizClass)) return 'IT/SW개발업';
    if (/건설|건축/.test(bizClass)) return '건설업';
    if (/숙박|호텔|모텔|여관|펜션|민박|게스트\s*하우스|리조트|콘도/.test(bizClass)) return '숙박업';
    if (/음식|식당|요식|카페|커피|외식|주점|제과점|분식|급식/.test(bizClass)) return '음식점업';
    if (/서비스|컨설팅|교육|광고/.test(bizClass)) return '서비스업';
    return '기타';
  }

  /** 빠진 칸 — 버튼 글자로 그대로 보여 준다(꺼진 버튼만 보이고 이유가 없던 것 · 2026-10-12 검수) */
  function regMissing(): string {
    if (!form.name.trim()) return '업체명을 적어 주세요';
    const n = form.biz_no.replace(/\D/g, '').length;
    if (n > 0 && n !== 10) return '사업자번호는 10자리예요 (모르면 비워 두세요)';
    return refMissing();
  }
  async function handleSave() {
    const cleanedBizNo = form.biz_no.replace(/\D/g, '');   // 사업자번호는 선택 — 휴대폰 간이 등록과 같은 규칙
    const miss = regMissing();
    if (miss) { onToast(miss, 'error'); return; }
    const isFp = referral === 'FP';
    const wmId = (isFp ? (firstFp && firstFp.wm_id ? firstFp.wm_id : (wmChoice && wmChoice !== 'none' ? wmChoice : '')) : '') || defaultWmId || '';   // 직접 거래처에서 열면 그 WM님(2026-10-29)
    const detail = referral === '세무사무소' ? { office: refDetail.office.trim(), person: refDetail.person.trim(), phone: refDetail.phone.trim() }
      : referral === '지인' ? { person: refDetail.person.trim(), phone: refDetail.phone.trim() }
        : referral === '직접 문의' ? (refDetail.how ? { how: refDetail.how } : {}) : {};
    setSaving(true);
    try {
      await api.createCompany({
        name: form.name, biz_no: cleanedBizNo,
        ceo: form.ceo, ceo_phone: form.ceo_phone, ceo_email: form.ceo_email,
        ceo_birth: matchValue.ceo_birth || null,
        region: matchValue.region, region_detail: matchValue.region_detail,
        biz_type: matchValue.biz_type, biz_type_detail: form.biz_type_detail, biz_types: matchValue.biz_types,
        company_scale: matchValue.company_scale, is_startup: !!matchValue.is_startup,
        employees: matchValue.employees.trim() !== '' ? Number(matchValue.employees) : null,
        sales_eok: matchValue.sales_eok.trim() !== '' ? Number(matchValue.sales_eok) : null,
        established_date: matchValue.established_date || null,
        address: form.address, corp_type: form.corp_type,
        fp_ids: isFp ? form.fp_ids : [], tags: mergeTags(form.tags, matchValue),
        referral_type: referral as Referral, referral_detail: detail,
        ...(matchValue.sectors_off.length ? { sectors_off: matchValue.sectors_off } : {}),   // 업종으로 자동 해당했지만 뺀 전용 대상 (2026-10-05f)
        established_year: form.established_year,
        company_reg_num: form.company_reg_num,
        wm_id: wmId || undefined,
        tm_status: '대기' as const, source: '직접등록',
      });
      // FP 에 WM 이 없어서 여기서 고른 WM → FP 에도 저장(다음부터 자동) — 업체 등록이 된 뒤에(등록 실패면 FP 도 그대로)
      let wmFail = false;
      if (needWm && firstFp && wmId) { try { await api.updateFp(firstFp.id, { wm_id: wmId }); } catch { wmFail = true; } }
      onToast(wmFail ? '업체는 등록했어요 · FP 에 WM 저장은 못 했어요(FP 관리에서 지정해 주세요)' : '업체가 등록되었습니다', wmFail ? 'error' : 'success');
      onSave();
    } catch (e: unknown) {
      // req()는 fetch 기반으로 Error(서버 error 메시지)만 throw — axios형 e.response 분기는 실행되지 않던 사문 코드
      onToast(e instanceof Error ? e.message : '등록 실패', 'error');
    } finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
      <div style={{ position: 'relative', width: 'min(540px, calc(100vw - 32px))', maxHeight: '90vh', background: C.white, borderRadius: R.lg, display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #111827, #1B3A5C)', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: T.lg, fontWeight: 700, color: '#fff' }}>🏢 업체 등록</div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: '#fff', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {/* 사업자번호 + 조회 */}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Field label="사업자번호 · 모르면 비워 두세요" value={form.biz_no}
                placeholder="000-00-00000"
                onChange={v => upd('biz_no', formatBizNo(v))} />
            </div>
            <div style={{ paddingTop: 19 }}>
              <Btn onClick={lookupBizNo} disabled={niceLoading}
                style={{ fontSize: T.sm, height: 38, padding: '0 14px' }}>
                {niceLoading ? '⏳' : '🔍 조회'}
              </Btn>
            </div>
          </div>
          {/* bizInfo display removed — lookup results fill form fields directly */}
          <Field label="업체명" value={form.name} required placeholder="상호명" onChange={v => upd('name', v)} />
          {/* 소개 경로 * — 4갈래 · 기본값 없음 (2026-10-12 입력 강제) */}
          <div style={{ marginBottom: 10, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 6 }}>소개 경로 <span style={{ color: C.red }}>*</span></label>
            <div role="radiogroup" aria-label="소개 경로" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
              {REFERRALS.map(r => {
                const on = referral === r;
                return (
                  <button key={r} type="button" role="radio" aria-checked={on} onClick={() => { if (r !== referral) setRefDetail({ office: '', person: '', phone: '', how: '' }); setReferral(r); }}
                    style={{ height: 36, borderRadius: 8, border: `1.5px solid ${on ? C.accent : C.border}`, background: on ? C.accentLt : C.white, color: on ? C.accent : C.text, fontSize: T.sm, fontWeight: on ? 700 : 500, cursor: 'pointer' }}>
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
          {referral === 'FP' && (<>
            {/* 담당 — FP · WM 를 같은 모양 줄로 한 상자에 (2026-10-26 지도사 「이 부분 조잡하다」 — 작은 칩 · 회색 안내 글 · 검색 칸이 제각각이던 것)
                FP 가 없으면 검색 칸만 · 있으면 줄 + 「FP 한 명 더」 (누르면 검색 칸) · WM 은 첫 FP 에서 자동이면 같은 줄 모양에 「자동」 */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 6 }}>담당 FP{form.fp_ids.length > 0 && firstFp && !needWm ? ' · WM' : ''} <span style={{ color: C.red }}>*</span></label>
              {form.fp_ids.length === 0 ? (
                <PersonPicker kind="fp" people={fpOptions} value="" exclude={form.fp_ids} clearAfterPick
                  onChange={v => { if (v && !form.fp_ids.includes(v)) upd('fp_ids', [...form.fp_ids, v]); }}
                  onCreated={p => { setLocalFps(prev => [...prev, p as FP]); onRefresh?.().catch(() => { /* 상위 갱신 실패는 이 모달 동작과 무관 */ }); }}
                  placeholder="FP 이름 · 소속 · 연락처로 찾기" onToast={onToast} />
              ) : (
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.white }}>
                  {form.fp_ids.map((fid, i) => {
                    const fp = allFps.find(f => f.id === fid);
                    return (
                      <div key={fid} style={personRow(i > 0)}>
                        <span aria-hidden style={avatar(C.accent)}>{(fp?.name || '?').slice(0, 1)}</span>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: T.md, fontWeight: 700, color: C.text }}>{fp?.name || fid} <span style={{ fontWeight: 500, color: C.sub, fontSize: T.sm }}>FP{i === 0 && form.fp_ids.length > 1 ? ' · 대표' : ''}</span></span>
                          <span style={{ display: 'block', fontSize: T.sm, color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[fp?.company, fp?.phone].filter(Boolean).join(' · ') || '소속 미입력'}</span>
                        </span>
                        <button type="button" onClick={() => upd('fp_ids', form.fp_ids.filter(id => id !== fid))} aria-label={`${fp?.name || 'FP'} 빼기`} style={rowBtn}>빼기</button>
                      </div>
                    );
                  })}
                  {firstFp && !needWm && (
                    <div style={personRow(true)}>
                      <span aria-hidden style={avatar(C.purple)}>{((fpWm?.name || firstFp.wm_name || '?')).slice(0, 1)}</span>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: T.md, fontWeight: 700, color: C.text }}>{fpWm?.name || firstFp.wm_name || ''} <span style={{ fontWeight: 500, color: C.sub, fontSize: T.sm }}>WM</span></span>
                        <span style={{ display: 'block', fontSize: T.sm, color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[fpWm?.company || firstFp.wm_company, `${firstFp.name} FP 담당 WM`].filter(Boolean).join(' · ')}</span>
                      </span>
                      <span style={{ fontSize: T.xs, fontWeight: 700, color: C.purple, background: C.purpleLt, borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap' }}>자동</span>
                    </div>
                  )}
                  <div style={{ borderTop: `1px solid ${C.border}`, padding: addFp ? 8 : 0 }}>
                    {addFp ? (
                      <PersonPicker kind="fp" people={fpOptions} value="" exclude={form.fp_ids} clearAfterPick
                        onChange={v => { if (v && !form.fp_ids.includes(v)) upd('fp_ids', [...form.fp_ids, v]); setAddFp(false); }}
                        onCreated={p => { setLocalFps(prev => [...prev, p as FP]); onRefresh?.().catch(() => { /* 상위 갱신 실패는 이 모달 동작과 무관 */ }); }}
                        placeholder="함께 맡을 FP 이름 · 소속 · 연락처로 찾기" onToast={onToast} />
                    ) : (
                      <button type="button" onClick={() => setAddFp(true)}
                        style={{ width: '100%', height: 40, border: 'none', background: 'none', color: C.accent, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>+ 함께 맡는 FP 추가</button>
                    )}
                  </div>
                </div>
              )}
            </div>
            {needWm && (
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 3 }}>{firstFp ? firstFp.name : ''} FP 의 WM{form.fp_ids.length > 1 ? ' (첫 FP 기준)' : ''} <span style={{ color: C.red }}>*</span> <span style={{ fontWeight: 400, color: C.muted }}>· 고르면 FP 에도 저장돼요</span></label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <PersonPicker kind="wm" people={allWms} value={wmChoice === 'none' ? '' : wmChoice} onChange={id => setWmChoice(id)}
                      onCreated={p => { setLocalWms(prev => [...prev, p as WM]); onRefresh?.().catch(() => { /* 상위 갱신 실패는 이 모달 동작과 무관 */ }); }}
                      placeholder="WM 이름 · 소속으로 찾기" onToast={onToast} />
                  </div>
                  <button type="button" aria-pressed={wmChoice === 'none'} onClick={() => setWmChoice(wmChoice === 'none' ? '' : 'none')}
                    style={{ height: 38, padding: '0 12px', borderRadius: 8, border: `1.5px solid ${wmChoice === 'none' ? C.accent : C.border}`, background: wmChoice === 'none' ? C.accentLt : C.white, color: wmChoice === 'none' ? C.accent : C.text, fontSize: T.sm, fontWeight: wmChoice === 'none' ? 700 : 500, cursor: 'pointer', flexShrink: 0 }}>
                    WM 없음
                  </button>
                </div>
              </div>
            )}
          </>)}
          {referral === '세무사무소' && (<>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 3 }}>세무사무소 <span style={{ color: C.red }}>*</span></label>
              <input list="reg-offices" value={refDetail.office} onChange={e => setRefDetail(p => ({ ...p, office: e.target.value }))} placeholder="사무소 이름"
                style={{ width: '100%', height: 38, padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box' }} />
              <datalist id="reg-offices">{offices.map(o => <option key={o} value={o} />)}</datalist>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><Field label="사무소 담당자" value={refDetail.person} onChange={v => setRefDetail(p => ({ ...p, person: v }))} /></div>
              <div style={{ flex: 1 }}><Field label="사무소 연락처" value={refDetail.phone} placeholder="010-" onChange={v => setRefDetail(p => ({ ...p, phone: v }))} /></div>
            </div>
          </>)}
          {referral === '지인' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><Field label="소개한 분" required value={refDetail.person} onChange={v => setRefDetail(p => ({ ...p, person: v }))} /></div>
              <div style={{ flex: 1 }}><Field label="소개한 분 연락처" value={refDetail.phone} placeholder="010-" onChange={v => setRefDetail(p => ({ ...p, phone: v }))} /></div>
            </div>
          )}
          {referral === '직접 문의' && (
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 6 }}>어떻게 알고 왔나요 <span style={{ fontWeight: 400, color: C.muted }}>· 선택</span></label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                {HOWS.map(h => {
                  const on = refDetail.how === h;
                  return (
                    <button key={h} type="button" aria-pressed={on} onClick={() => setRefDetail(p => ({ ...p, how: on ? '' : h }))}
                      style={{ height: 32, borderRadius: 16, border: `1px solid ${on ? C.accent : C.border}`, background: on ? C.accentLt : C.white, color: on ? C.accent : C.sub, fontSize: T.xs, fontWeight: on ? 700 : 500, cursor: 'pointer' }}>
                      {h}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div style={{ borderTop: `1px solid ${C.border}`, margin: '4px 0 10px' }} />
          {/* 지역·업종(대분류)은 아래 「매칭 정보」(CompanyMatchFields)가 다룬다 — 중복 제거, 소분류만 별도 유지 */}
          <Field label="업종 소분류" value={form.biz_type_detail} placeholder="업종 소분류 (예: 정밀화학소재 제조업)"
            onChange={v => upd('biz_type_detail', v)} />
          <Field label="대표자" value={form.ceo} onChange={v => upd('ceo', v)} />
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><Field label="대표 연락처" value={form.ceo_phone} placeholder="010-" onChange={v => upd('ceo_phone', v)} /></div>
            <div style={{ flex: 1 }}><Field label="이메일" value={form.ceo_email} onChange={v => upd('ceo_email', v)} /></div>
          </div>
          <Field label="주소" value={form.address} onChange={v => upd('address', v)} />
          <Field label="법인등록번호" value={form.company_reg_num} placeholder="000000-0000000" onChange={v => upd('company_reg_num', v)} />
          {/* 매칭 정보 — CompanyMatchFields 공용(지역·업종·규모·창업여부·대표생년월일·직원수·매출·설립일·분류). 사업자번호 조회 시 개업일/업태/주소에서 자동 채움 */}
          <div style={{ marginBottom: 10, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
            <div onClick={() => setMatchOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: matchOpen ? 10 : 0 }}>
              <span style={{ fontSize: T.sm, fontWeight: 700, color: C.text }}>🏛️ 매칭 정보 <span style={{ fontWeight: 400, color: C.sub, fontSize: T.xs }}>(정부지원사업 매칭에 쓰입니다)</span></span>
              <span style={{ fontSize: 11, color: C.sub }}>{matchOpen ? '접기 ▲' : '펼치기 ▼'}</span>
            </div>
            {matchOpen && (
              <CompanyMatchFields value={matchValue} onChange={patch => setMatchValue(prev => ({ ...prev, ...patch }))} layout="compact" companyName={form.name} />
            )}
          </div>
          {/* 태그 */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 3 }}>태그</label>
            <TagEditor tags={form.tags} onChange={v => upd('tags', [...v.required, ...v.recommended, ...v.interests])} flat />
          </div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8 }}>
          <Btn variant="secondary" onClick={onClose} style={{ flex: 1 }}>취소</Btn>
          <Btn onClick={handleSave} disabled={saving} style={{ flex: 2, background: regMissing() ? '#E5E7EB' : 'linear-gradient(135deg, #111827, #1B3A5C)', color: regMissing() ? '#6B7280' : '#fff', fontWeight: 700, borderRadius: 10, border: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {saving ? '⏳ 등록 중...' : regMissing() || '✅ 업체 등록'}
          </Btn>
        </div>
      </div>
    </div>
  );
}
